import { z } from "zod";
import { type LicenseResponse, type LicenseUpdate, schemas } from "@/api";

/** The edit form works on strings; blanks mean "unlimited" / "never" / "none". */
export interface LicenseFormValues {
  licenseName: string;
  seatLimit: string;
  expiresAt: string;
  exemptDomains: string;
  notes: string;
}

export const licenseFormSchema = (seatLimitInvalid: string) =>
  z.object({
    licenseName: z.string().trim(),
    seatLimit: z.string().trim().regex(/^\d*$/, seatLimitInvalid),
    expiresAt: z.string(),
    exemptDomains: z.string(),
    notes: z.string(),
  });

export function toFormValues(license: LicenseResponse): LicenseFormValues {
  return {
    licenseName: license.licenseName ?? "",
    seatLimit: license.seatLimit == null ? "" : String(license.seatLimit),
    expiresAt: license.expiresAt ? license.expiresAt.slice(0, 10) : "",
    exemptDomains: (license.exemptDomains ?? []).join(", "),
    notes: license.notes ?? "",
  };
}

const blankToNull = (value: string) => value.trim() || null;

/** Form values → the PUT body, checked against the generated LicenseUpdate schema. */
export function toUpdateBody(values: LicenseFormValues): LicenseUpdate {
  const seats = values.seatLimit.trim();
  return schemas.zLicenseUpdate.parse({
    licenseName: blankToNull(values.licenseName),
    seatLimit: seats ? Number.parseInt(seats, 10) : null,
    // A date-only input: the license runs through the end of that day (UTC).
    expiresAt: values.expiresAt ? `${values.expiresAt}T23:59:59Z` : null,
    exemptDomains: values.exemptDomains
      .split(",")
      .map((d) => d.trim().toLowerCase())
      .filter(Boolean),
    notes: blankToNull(values.notes),
  });
}
